/**
 * Embed Auth Context
 * 
 * Provides authentication context for iframe/embed mode.
 * Flow: postMessage (access_token) → store → authenticated
 *
 * A client portal that signs its people in at the project's own IdP sends
 * SET_ID_TOKEN instead: the ID token is exchanged at our backend for the same
 * access token the office would have sent, and stored where SET_TOKEN's goes.
 *
 * The office may send a bundle instead (SET_TOKENS {tokens: {'zehn-docs': …,
 * 'zehn-hr': …}}): docs' token is used as above, the other modules' tokens are
 * kept per tab for asking those modules for data as the same person (templates
 * filled from their records). Through an IdP portal the same ID token is traded
 * at each such module too.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    storeAccessToken,
    getAccessToken,
    getProjectUuid,
    clearEmbedTokens,
    hasValidEmbedTokens,
    initFromStorage,
    storeModuleToken,
    storeModuleTokens,
    DOCS_MODULE_CODE,
} from '@/shared/utils/embedTokenManager';
import { fetchTemplateSources } from '@/shared/utils/templateSources';
import { Context } from '@/shared/contexts/GlobalContext';
import { useTranslation } from 'react-i18next';
import { EMBED_USER_ME } from '@/shared/utils/urls';
import { useGetRequest } from '@/shared/hooks/requests';
import { captureParentOrigin } from '@/shared/utils/embedParentOrigin';
import {
    exchangeIdToken,
    isTrustedIdTokenSender,
    scheduleRenewal,
} from '@/shared/utils/idTokenExchange';
import { domain } from '@/shared/utils/request';
import { UI_LANGUAGE_CODES } from '@/shared/utils/languages.js';
import { buildInfo } from '@/shared/buildInfo';
import { publishIdToken, publishTokenBundle, setAipAuthRefresher } from '@/shared/utils/aipPanel';


// Check if running in iframe
export const isInIframe = () => {
    try {
        // The in-editor agent panel is same-origin and reads the session
        // from localStorage directly — it must not wait for the office
        // portal's postMessage token handshake (its parent is Collabora,
        // which will never send one).
        if (window.location.pathname.startsWith('/agent-panel')) return false;
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
};

// Sign-in through the project's own IdP (SET_ID_TOKEN). Module state, not
// provider state: the app mounts one provider and every embed page mounts
// another (withEmbedAuth), all of them hear the same message, and their effects
// re-run on renders. One exchange per ID token, one renewal timer and one
// "an ID token has arrived" flag serve them all.
const idTokenSession = { started: false, inflight: null, renewalTimer: null };

// Everything the office's SET_TOKEN writes: auth.js reads `iframeToken` first
// (iframeToken > guestToken > token), the embed interceptor the embed_* keys.
const clearStoredToken = () => {
    clearEmbedTokens();
    localStorage.removeItem('iframeToken');
};

// The same ID token, traded at every module that fills this project's
// templates, so docs' UI can ask it for values as this person. Best effort: a
// module that refuses (or is down) just stays unavailable for filling.
const exchangeSourceTokens = async (tenant, idToken) => {
    const sources = await fetchTemplateSources({ refresh: true });
    await Promise.all(sources.map(async ({ source, apiUrl }) => {
        try {
            const result = await exchangeIdToken(apiUrl, { tenant, idToken });
            storeModuleToken(source, result.access_token);
        } catch (error) {
            storeModuleToken(source, null);
            console.warn(`[EmbedAuth] ${source} did not take the ID token:`, error?.code || error?.message);
        }
    }));
};

const exchangeIdTokenOnce = (event) => {
    const { tenant, id_token: idToken } = event.data;
    const key = `${tenant} ${idToken}`;
    if (idTokenSession.inflight?.key === key) return idTokenSession.inflight;

    // A token left in this tab may belong to whoever used the portal before;
    // the first ID token decides who we are, so drop it and wait. Renewals keep
    // the current token until the next one lands.
    const first = !idTokenSession.started;
    idTokenSession.started = true;
    if (first) clearStoredToken();

    const promise = exchangeIdToken(domain, { tenant, idToken }).then(
        (result) => {
            // Where SET_TOKEN puts the office's token, so it takes the same
            // precedence everywhere (axios, auth(), ProtectedRoute).
            storeAccessToken({ token: result.access_token, expiresIn: result.expires_in });
            clearTimeout(idTokenSession.renewalTimer);
            idTokenSession.renewalTimer = scheduleRenewal(result.expires_in, event.origin);
            exchangeSourceTokens(tenant, idToken);
            return result;
        },
        (error) => {
            clearStoredToken();
            window.parent.postMessage(
                { type: 'OIDC_ERROR', code: error.code || 'exchange_failed', message: error.message },
                event.origin
            );
            throw error;
        }
    );
    const entry = { key, first, promise };
    idTokenSession.inflight = entry;
    const settle = () => {
        if (idTokenSession.inflight === entry) idTokenSession.inflight = null;
    };
    promise.then(settle, settle);
    return entry;
};

// Context
const EmbedAuthContext = createContext(null);

// Provider Component
export function EmbedAuthProvider({ children }) {
    const navigate = useNavigate();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [projectUuid, setProjectUuid] = useState(null);
    // Set when a portal's ID token could not be traded for access (no group
    // grants this module, the token was refused…).
    const [accessError, setAccessError] = useState(null);
    const { setLang, setTheme } = useContext(Context);
    const { i18n, t } = useTranslation();

    const tokenRefreshTimerRef = useRef(null);
    // Whether this provider has signed in once (see handleAccessToken).
    const signedInRef = useRef(false);
    const isIframe = isInIframe();

    // Hook for fetching user data
    const getUserRequest = useGetRequest({ url: EMBED_USER_ME });

    // Request token refresh from parent
    const requestTokenRefresh = useCallback(() => {
        if (!isIframe) return;

        console.log('[EmbedAuth] Requesting token refresh from parent...');
        window.parent.postMessage({ type: 'TOKEN_EXPIRED' }, '*');
    }, [isIframe]);

    // Fetch user data from embed/v1/me endpoint
    const fetchUserData = useCallback(async () => {
        const token = getAccessToken();
        if (!token) {
            console.log('[EmbedAuth] No token available for fetching user data');
            return;
        }

        try {
            console.log('[EmbedAuth] Fetching user data from embed/v1/me...');
            const { response, success } = await getUserRequest.request({ showError: false });

            if (success && response) {
                const userData = {
                    id: response.id,
                    externalUserId: response.externalUserId,
                    email: response.email,
                    displayName: response.displayName,
                    userType: response.userType,
                    externalProvider: response.externalProvider,
                    externalProjectId: response.externalProjectId,
                };

                // Cache in localStorage
                localStorage.setItem('embed_user', JSON.stringify(userData));
                localStorage.setItem('userId', String(userData.id));
                if (userData.displayName) {
                    localStorage.setItem('userName', userData.displayName);
                }
                if (userData.email) {
                    localStorage.setItem('userEmail', userData.email);
                }

                console.log('[EmbedAuth] User data fetched and cached:', userData);
            }
        } catch (error) {
            console.error('[EmbedAuth] Failed to fetch user data:', error);
        }
    }, [getUserRequest]);

    // Handle token from parent - no exchange needed, use directly
    const handleAccessToken = useCallback(async (token) => {
        // The loading screen is for the first sign-in only. EmbedLayout swaps
        // the whole page for it, so showing it on a refresh (every ~14 min)
        // unmounted an open editor and closed its Collabora session.
        const firstSignIn = !signedInRef.current;
        if (firstSignIn) setIsLoading(true);
        setError(null);

        console.log('[EmbedAuth] Storing access token...');

        const result = storeAccessToken({ token });

        if (result.success) {
            signedInRef.current = true;
            setIsAuthenticated(true);
            setProjectUuid(result.projectUuid);
            setError(null);

            // Fetch user data after token is stored
            await fetchUserData();

            // Schedule proactive token refresh (1 minute before expiration)
            if (result.expiresIn && result.expiresIn > 120) {
                const refreshTime = (result.expiresIn - 60) * 1000;

                if (tokenRefreshTimerRef.current) {
                    clearTimeout(tokenRefreshTimerRef.current);
                }

                tokenRefreshTimerRef.current = setTimeout(() => {
                    console.log('[EmbedAuth] Proactive token refresh...');
                    requestTokenRefresh();
                }, refreshTime);
            }
        } else {
            signedInRef.current = false;
            setIsAuthenticated(false);
            setError('Failed to store token');
        }

        setIsLoading(false);
    }, [requestTokenRefresh, fetchUserData]);

    // The doc agent panel's sign-in rides on the office's bundle; when the
    // panel needs one this page lacks, the office is asked for new tokens.
    useEffect(() => {
        if (isIframe) setAipAuthRefresher(requestTokenRefresh);
    }, [isIframe, requestTokenRefresh]);

    // Listen for messages from parent
    useEffect(() => {
        if (!isIframe) {
            setIsLoading(false);
            return;
        }

        const handleIdToken = (event) => {
            const { first, promise } = exchangeIdTokenOnce(event);
            if (first) {
                setIsAuthenticated(false);
                setIsLoading(true);
            }
            promise
                .then(() => {
                    console.log('[EmbedAuth] Exchanged ID token');
                    setAccessError(null);
                    setError(null);
                    setIsAuthenticated(true);
                    setProjectUuid(getProjectUuid());
                    setIsLoading(false);
                    fetchUserData();
                })
                .catch((exchangeError) => {
                    console.warn('[EmbedAuth] ID token refused:', exchangeError.code);
                    setIsAuthenticated(false);
                    setAccessError(exchangeError);
                    setIsLoading(false);
                });
        };

        const handleMessage = (event) => {
            // Only the window that frames us may drive this context. A sibling
            // frame, popup or opener can also postMessage here; without this
            // gate a SET_TOKEN from any of them would inject a session (session
            // fixation). event.source is stamped by the browser and unforgeable.
            if (event.source !== window.parent) return;

            // Record who is framing us, so route changes have somewhere to go.
            captureParentOrigin(event);

            const { type, token, userId, userName, userEmail } = event.data || {};

            switch (type) {
                case 'SET_ID_TOKEN':
                    // Unlike SET_TOKEN, only from a framer we were told about
                    // (VITE_EMBED_ORIGINS): it signs us in as somebody.
                    if (event.data.id_token && isTrustedIdTokenSender(event)) {
                        handleIdToken(event);
                        // The editor's aip panel signs in with the same ID token.
                        publishIdToken(event.data.tenant, event.data.id_token);
                    }
                    break;

                case 'SET_TOKEN':
                    if (token) {
                        handleAccessToken(token);
                    }
                    break;

                case 'SET_TOKENS': {
                    // A bundle: docs' own token plus other modules' (kept for
                    // asking them for data). A module missing from a fresh
                    // bundle loses its token.
                    const bundle = event.data.tokens || {};
                    storeModuleTokens(bundle, { replace: true });
                    publishTokenBundle(bundle);
                    const docsToken = bundle[DOCS_MODULE_CODE] ?? token;
                    if (docsToken) {
                        handleAccessToken(docsToken);
                    }
                    break;
                }

                case 'TOKEN_REFRESHED': {
                    // The same bundle shape when the first one was a bundle; a
                    // module missing from a refresh keeps its token.
                    if (event.data.tokens) {
                        storeModuleTokens(event.data.tokens);
                        publishTokenBundle(event.data.tokens);
                    }
                    const docsToken = event.data.tokens?.[DOCS_MODULE_CODE] ?? token;
                    if (docsToken) {
                        console.log('[EmbedAuth] Received refreshed token');
                        handleAccessToken(docsToken);
                    }
                    break;
                }

                case 'SET_THEME':
                case 'THEME_CHANGE':
                    const theme = event.data.theme || event.data.payload;
                    if (theme) {
                        console.log("[zehn-docs] Received theme:", theme);
                        setTheme(theme);
                    }
                    break;

                case 'SET_LANG':
                case 'LANGUAGE_CHANGE':
                    const lang = event.data.lang || event.data.payload;
                    // Only codes this app has a locale for; the office may
                    // speak one we do not (then the current language stays).
                    if (lang && UI_LANGUAGE_CODES.includes(lang)) {
                        console.log("[zehn-docs] Received lang:", lang);
                        setLang(lang);
                        i18n.changeLanguage(lang);
                    }
                    break;

                case 'MODULE_INFO_REQUEST':
                    // The host asks again (it may have started listening after
                    // our first announcement). Reply to the asking origin only.
                    window.parent.postMessage(moduleInfoMessage(), event.origin);
                    break;

                default:
                    break;
            }
        };

        window.addEventListener('message', handleMessage);

        // Build identity for the host's About dialog — sent beside every
        // IFRAME_READY. Not a secret, so '*' like the ready ping.
        const moduleInfoMessage = () => ({
            type: 'MODULE_INFO',
            module: 'zehn-docs',
            version: buildInfo.version,
            build: buildInfo.build,
            git_sha: buildInfo.gitSha,
            build_time: buildInfo.buildTime,
        });

        const notifyReady = () => {
            console.log('[EmbedAuth] Notifying parent: IFRAME_READY');
            window.parent.postMessage({ type: 'IFRAME_READY' }, '*');
            window.parent.postMessage(moduleInfoMessage(), '*');
        };

        // Notify immediately and then periodically until we get a token. A
        // portal that hands us ID tokens is answered by exchanging them, so once
        // one has arrived there is nothing left to ping for — and pinging on
        // every re-run of this effect would make it send the token again.
        if (!idTokenSession.started) notifyReady();
        const interval = setInterval(() => {
            if (!getAccessToken() && !idTokenSession.started) {
                notifyReady();
            } else {
                clearInterval(interval);
            }
        }, 1000);

        // Try to restore from storage
        initFromStorage();
        if (hasValidEmbedTokens()) {
            console.log('[EmbedAuth] Restored from storage');
            setIsAuthenticated(true);
            setProjectUuid(getProjectUuid());
            setIsLoading(false);
            clearInterval(interval);
        }

        return () => {
            window.removeEventListener('message', handleMessage);
            clearInterval(interval);
            if (tokenRefreshTimerRef.current) {
                clearTimeout(tokenRefreshTimerRef.current);
            }
            // idTokenSession.renewalTimer outlives this effect on purpose: it
            // re-runs on renders, and the renewal belongs to the page.
        };
    }, [isIframe, handleAccessToken, fetchUserData]);

    // Logout / clear
    const logout = useCallback(() => {
        clearEmbedTokens();
        setIsAuthenticated(false);
        setProjectUuid(null);
    }, []);

    const value = {
        isIframe,
        isAuthenticated,
        isLoading,
        error,
        projectUuid,
        accessToken: getAccessToken(),
        requestTokenRefresh,
        logout,
    };

    if (isIframe && accessError) {
        const noAccess = accessError.code === 'no_module_access';
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, height: '100dvh', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
                <strong>
                    {noAccess ? t('common:embed.noAccessTitle') : t('common:embed.signInFailedTitle')}
                </strong>
                <span>{noAccess ? t('common:embed.noAccessHint') : accessError.message}</span>
            </div>
        );
    }

    if (isIframe && isLoading) {
        return (
            <div style={{ display: 'flex', height: '100dvh', alignItems: 'center', justifyContent: 'center' }}>
                Loading Application...
            </div>
        );
    }

    return (
        <EmbedAuthContext.Provider value={value}>
            {children}
        </EmbedAuthContext.Provider>
    );
}

// Hook
export function useEmbedAuth() {
    const context = useContext(EmbedAuthContext);
    if (!context) {
        throw new Error('useEmbedAuth must be used within EmbedAuthProvider');
    }
    return context;
}

export default EmbedAuthContext;
